// client/src/components/mobile/MobileMenu.js
// ================================================================
// WHOLESALEHUB MOBILE MENU — UPDATED
// Added Catalog Discounts, renamed Discounts → Customer Discounts
// ================================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Home, Package, ShoppingCart, FileText, User, BarChart,
  Heart, FileStack, Upload, CreditCard, BookOpen, HelpCircle,
  LogOut, Lock, Percent, Zap, Settings, Tag, Users, DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../common/LanguageSwitcher';

const MobileMenu = ({ isOpen, onClose, user, currentPath = '/' }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    onClose();
  };

  const displayName = user?.profile?.businessName || 
                      user?.profile?.companyName || 
                      user?.email?.split('@')[0] || 
                      t('mobileMenu.guestUser');
  const displayEmail = user?.email || 'guest@example.com';

  const getRoleText = (role) => {
    switch (role) {
      case 'business': return t('mobileMenu.roles.business');
      case 'wholesaler': return t('mobileMenu.roles.wholesaler');
      case 'admin': return t('mobileMenu.roles.admin');
      default: return t('mobileMenu.roles.user');
    }
  };

  // Reusable menu item
  const MenuItem = ({ path, icon: Icon, label, accent }) => (
    <button
      onClick={() => handleNavigation(path)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-0.5 transition-colors ${
        currentPath === path
          ? 'bg-blue-50 text-blue-700'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${
        currentPath === path ? 'text-blue-600' : accent ? `text-${accent}-500` : 'text-gray-400'
      }`} />
      <span className="font-medium text-sm">{label}</span>
    </button>
  );

  // Section header
  const SectionLabel = ({ children }) => (
    <div className="px-4 pt-4 pb-1.5">
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{children}</span>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Slide-in Menu */}
      <div className="fixed top-0 left-0 bottom-0 w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 lg:hidden flex flex-col">
        
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <span className="text-lg font-bold text-blue-600">WholesaleHub</span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('mobileMenu.close')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── User Profile ── */}
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 text-sm truncate">{displayName}</div>
              <div className="text-xs text-gray-500 truncate">{displayEmail}</div>
              <div className="inline-flex items-center mt-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                {getRoleText(user?.role)}
              </div>
            </div>
          </div>
        </div>

        {/* ── Menu Items (Scrollable) ── */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          
          {/* ═══ BUSINESS MENU ═══ */}
          {user?.role === 'business' && (
            <>
              <SectionLabel>{t('mobileMenu.sections.main', 'Main')}</SectionLabel>
              <MenuItem path="/dashboard" icon={Home} label={t('mobileMenu.nav.dashboard')} />
              <MenuItem path="/wholesalers" icon={Package} label={t('mobileMenu.nav.products')} />
              <MenuItem path="/my-orders" icon={FileText} label={t('mobileMenu.nav.orders')} />

              <SectionLabel>{t('mobileMenu.sections.quickAccess', 'Quick Access')}</SectionLabel>
              <MenuItem path="/business/favorites" icon={Heart} label={t('mobileMenu.nav.favorites')} />
              <MenuItem path="/business/templates" icon={FileStack} label={t('mobileMenu.nav.templates')} />
              <MenuItem path="/offers" icon={Zap} label={t('mobileMenu.nav.offers', 'Offers')} accent="red" />

              <SectionLabel>{t('mobileMenu.sections.account', 'Account')}</SectionLabel>
              <MenuItem path="/business/analytics" icon={BarChart} label={t('mobileMenu.nav.analytics')} />
              <MenuItem path="/business/profile" icon={User} label={t('mobileMenu.nav.profile')} />
            </>
          )}

          {/* ═══ WHOLESALER MENU ═══ */}
          {user?.role === 'wholesaler' && (() => {
            const subRole = user?.teamContext?.subRole || 'manager';
            const isManager = subRole === 'manager';
            const isOffice = subRole === 'office';
            const isSalesRep = subRole === 'sales_rep';
            return (
              <>
                <SectionLabel>{t('mobileMenu.sections.main', 'Main')}</SectionLabel>
                <MenuItem path="/wholesaler/dashboard" icon={Home} label={t('mobileMenu.nav.dashboard')} />
                <MenuItem path="/wholesaler/orders" icon={FileText} label={t('mobileMenu.nav.orders')} />
                {(isManager || isSalesRep) && (
                  <MenuItem path="/wholesaler/place-order" icon={ShoppingCart} label={t('mobileMenu.nav.placeOrder', 'Place Order')} />
                )}

                {/* Products — Manager + Office only */}
                {(isManager || isOffice) && (
                  <>
                    <SectionLabel>{t('mobileMenu.sections.products', 'Products')}</SectionLabel>
                    <MenuItem path="/wholesaler/products" icon={Package} label={t('mobileMenu.nav.productList', 'Product List')} />
                    <MenuItem path="/wholesaler/products/bulk-upload" icon={Upload} label={t('mobileMenu.nav.bulkUpload', 'Bulk Upload')} />
                  </>
                )}

                {/* Sales Tools — Manager + Office only */}
                {(isManager || isOffice) && (
                  <>
                    <SectionLabel>{t('mobileMenu.sections.salesTools', 'Sales Tools')}</SectionLabel>
                    <MenuItem path="/wholesaler/flash-offers" icon={Zap} label={t('mobileMenu.nav.flashOffers', 'Flash Offers')} accent="red" />
                    <MenuItem path="/wholesaler/catalog-discounts" icon={Tag} label={t('mobileMenu.nav.catalogDiscounts', 'Catalog Discounts')} accent="teal" />
                    <MenuItem path="/wholesaler/discounts" icon={Percent} label={t('mobileMenu.nav.customerDiscounts', 'Customer Discounts')} accent="orange" />
                    <MenuItem path="/wholesaler/product-discounts" icon={Tag} label={t('mobileMenu.nav.productDiscounts', 'Product Discounts')} accent="purple" />
                    <MenuItem path="/wholesaler/catalog-requests" icon={Lock} label={t('mobileMenu.nav.catalogRequests')} accent="yellow" />
                  </>
                )}

                <SectionLabel>{t('mobileMenu.sections.account', 'Account')}</SectionLabel>
                {/* Team — Manager only */}
                {isManager && (
                  <MenuItem path="/wholesaler/team" icon={Users} label={t('mobileMenu.nav.team', 'Team')} />
                )}
                {/* Subscription — Manager only */}
                {isManager && (
                  <MenuItem path="/wholesaler/subscription" icon={CreditCard} label={t('mobileMenu.nav.subscription')} />
                )}
                <MenuItem path="/wholesaler/analytics" icon={BarChart} label={t('mobileMenu.nav.analytics')} />
                <MenuItem path="/wholesaler/profile" icon={User} label={t('mobileMenu.nav.profile')} />
              </>
            );
          })()}

          {/* ═══ ADMIN MENU ═══ */}
          {user?.role === 'admin' && (
            <>
              <SectionLabel>{t('mobileMenu.sections.main', 'Main')}</SectionLabel>
              <MenuItem path="/admin/dashboard" icon={Home} label={t('mobileMenu.nav.dashboard')} />
              <MenuItem path="/admin/users" icon={User} label={t('mobileMenu.nav.users')} />
              <MenuItem path="/admin/orders" icon={FileText} label={t('mobileMenu.nav.orders')} />
              <MenuItem path="/admin/billing" icon={CreditCard} label={t('mobileMenu.nav.billing', 'Billing')} />
              <MenuItem path="/admin/tools" icon={Package} label={t('mobileMenu.nav.tools')} />
              <MenuItem path="/admin/settings" icon={Settings} label={t('mobileMenu.nav.settings')} />
            </>
          )}

          {/* ── Help & Support ── */}
          <SectionLabel>{t('mobileMenu.sections.support', 'Support')}</SectionLabel>
          <MenuItem path="/pricing" icon={DollarSign} label={t('mobileMenu.nav.pricing', 'Pricing')} accent="green" />
          <MenuItem path="/help" icon={BookOpen} label={t('mobileMenu.nav.helpCenter')} />
          <MenuItem path="/faq" icon={HelpCircle} label={t('mobileMenu.nav.faq')} />
        </nav>

        {/* ── Bottom: Language + Logout ── */}
        <div className="border-t border-gray-100 bg-white px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <LanguageSwitcher dropdownDirection="up" />
          </div>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium text-sm"
          >
            <LogOut className="w-4 h-4" />
            {t('mobileMenu.logout')}
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;